import React, { useState, useEffect } from "react";

const ContactUsFormSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    quantity: "",
    referenceFile: null as File | null,
    acceptance: false
  });

  const [showPrivacyNotification, setShowPrivacyNotification] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'file') {
      const fileInput = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: fileInput.files ? fileInput.files[0] : null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    // Enhanced email validation
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Enhanced phone validation (10-15 digits, allows +, spaces, hyphens)
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else {
      const phoneDigits = formData.phone.replace(/[\s\-+()]/g, '');
      if (!/^\d{10,15}$/.test(phoneDigits)) {
        errors.phone = "Please enter a valid phone number (10-15 digits)";
      }
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }

    // Enhanced quantity validation
    if (!formData.quantity.trim()) {
      errors.quantity = "Quantity is required";
    } else if (!/^\d+$/.test(formData.quantity.trim())) {
      errors.quantity = "Please enter a valid number";
    }

    if (!formData.acceptance) {
      errors.acceptance = "You must agree to data collection";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Show privacy notification if checkbox is not checked
    if (!formData.acceptance) {
      setShowPrivacyNotification(true);
      return;
    }

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setShowErrorNotification(false);
    setShowSuccessNotification(false);

    try {
      const apiUrl: any = process.env.REACT_APP_CONTACT_API_URL;

      const response = await fetch(`${apiUrl}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          quantity: formData.quantity,
          message: formData.message,
          from: "Modli 3D Contact Form"
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "There was an error trying to send your message. Please try again.");
      }

      // Show success notification
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        quantity: "",
        referenceFile: null,
        acceptance: false
      });
      setFormErrors({});
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "An error occurred.");
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hide privacy notification when checkbox is checked
  useEffect(() => {
    if (formData.acceptance && showPrivacyNotification) {
      setShowPrivacyNotification(false);
    }
  }, [formData.acceptance, showPrivacyNotification]);

  return (
    <>
      {/* Privacy Policy Notification */}
      {showPrivacyNotification && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#ee5536',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: '"Open Sans", sans-serif',
          animation: 'slideInUp 0.3s ease-out',
          whiteSpace: 'nowrap'
        }}>
          Please accept the terms of our Privacy Policy.
        </div>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: '"Open Sans", sans-serif',
          animation: 'slideInUp 0.3s ease-out',
          maxWidth: '400px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>✅</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>Thank you!</div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>We've received your request and will get back to you shortly.</div>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {showErrorNotification && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: '"Open Sans", sans-serif',
          animation: 'slideInUp 0.3s ease-out',
          maxWidth: '400px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>Something went wrong.</div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>Please try again or contact us directly.</div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .form-error {
          color: #ff6b35;
          font-size: 12px;
          margin-top: 5px;
          display: block;
        }
        .form-field-error {
          border-color: #ff6b35 !important;
        }
        .submit-error {
          color: #ff6b35;
          font-size: 14px;
          margin-top: 10px;
          text-align: center;
          font-weight: 500;
        }
        ::placeholder {
          color: #bbb !important;
          opacity: 1;
        }
        :-ms-input-placeholder {
          color: #bbb !important;
        }
        ::-ms-input-placeholder {
          color: #bbb !important;
        }
      `}</style>
      <section className="elementor-section elementor-top-section elementor-element elementor-element-43efb56 elementor-section-boxed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited" data-id="43efb56" data-element_type="section" style={{marginTop: "100px"}}>
        <div className="elementor-container elementor-column-gap-extended">
          <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-c637b50 sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited" data-id="c637b50" data-element_type="column">
            <div className="elementor-widget-wrap elementor-element-populated">
              <div className="elementor-element elementor-element-e06ce4e sc_height_huge sc_fly_static elementor-widget elementor-widget-spacer trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited" data-id="e06ce4e" data-element_type="widget" data-widget_type="spacer.default">
                <div className="elementor-widget-container">
                  <div className="elementor-spacer">
                    <div className="elementor-spacer-inner"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="elementor-section elementor-top-section elementor-element elementor-element-4c6fba1 elementor-section-boxed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
        <div className="elementor-container elementor-column-gap-extended">
          <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-af3fff6 sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
            <div className="elementor-widget-wrap elementor-element-populated">
              <div className="elementor-element elementor-element-75885101 sc_fly_static elementor-widget elementor-widget-trx_sc_title trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">
                  <div className="sc_title sc_title_default"><span className="sc_item_subtitle sc_title_subtitle sc_item_subtitle_above sc_item_title_style_default">Contact Us</span><h1 className="sc_item_title sc_title_title sc_item_title_style_default sc_item_title_tag"><span className="sc_item_title_text">Have questions? Get in touch!</span></h1><div className="sc_item_descr sc_title_descr"><p>Share the basics and we’ll take it from there.</p>
                  </div></div>				</div>
              </div>
              <div className="elementor-element elementor-element-67538fa2 sc_fly_static elementor-widget elementor-widget-spacer trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">
                  <div className="elementor-spacer">
                    <div className="elementor-spacer-inner"></div>
                  </div>
                </div>
              </div>
              <div className="elementor-element elementor-element-3b700e65 sc_fly_static elementor-widget elementor-widget-trx_sc_icons trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">

                  <div className="elementor-element elementor-element-3b700e65 sc_fly_static elementor-widget elementor-widget-trx_sc_icons trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited" data-id="3b700e65" data-element_type="widget" data-widget_type="trx_sc_icons.default">
                    <div className="elementor-widget-container">
                      <div className="sc_icons sc_icons_simple sc_icons_size_small sc_align_none"><div className="sc_icons_item with_more"><div className="sc_icons_icon sc_icon_type_ icon-marker" style={{ color: "#EE5536" }}><span className="sc_icon_type_ icon-marker" style={{ color: "#EE5536" }}></span></div><div className="sc_icons_item_details"><div className="sc_icons_item_description">
                        <span>Jothi Towers, Plot No 32/33,
                          Krishna Avenue First Street,<br />
                          Sri Krishna Avenue, Rajakilpakkam,
                          Chennai - 600073.<br />
                          (Behind Hansa Garden)</span></div></div></div><div className="sc_icons_item sc_icons_item_linked with_more"><div className="sc_icons_icon sc_icon_type_ icon-iphone" style={{ color: "#EE5536" }}><span className="sc_icon_type_ icon-iphone" style={{ color: "#EE5536" }}></span></div><div className="sc_icons_item_details"><a href="tel:18408412569" className="sc_icons_item_more_link">
                            <span className="link_text">+91 9150027017</span><span className="link_icon"></span>
                          </a><a href="tel:18408412569" className="sc_icons_item_link">
                            </a></div></div><div className="sc_icons_item with_more"><div className="sc_icons_icon sc_icon_type_ icon-envelope-2" style={{ color: "#EE5536" }}><span className="sc_icon_type_ icon-envelope-2" style={{ color: "#EE5536" }}></span></div><div className="sc_icons_item_details"><div className="sc_icons_item_description"><span><a href="mailto:info@email.com" className="underline_hover">info@modli3d.com</a></span></div></div></div></div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-5d36ce8 sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
            <div className="elementor-widget-wrap elementor-element-populated">
              <div className="elementor-element elementor-element-5abaf028 sc_fly_static elementor-widget elementor-widget-spacer trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">
                  <div className="elementor-spacer">
                    <div className="elementor-spacer-inner"></div>
                  </div>
                </div>
              </div>
              <div className="elementor-element elementor-element-4075b314 sc_fly_static elementor-widget elementor-widget-trx_sc_contact_form_7 trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">
                  <div className="wpcf7 js alert_inited" id="wpcf7-f1324-p1453-o1" lang="en-US" dir="ltr">
                    <div className="screen-reader-response">
                      <p role="status" aria-live="polite" aria-atomic="true"></p>
                      <ul></ul>
                    </div>
                    <form onSubmit={handleSubmit} className="wpcf7-form init" aria-label="Contact form" noValidate>
                      <fieldset className="hidden-fields-container">
                        <input type="hidden" name="_wpcf7" value="1324" />
                        <input type="hidden" name="_wpcf7_version" value="6.1" />
                        <input type="hidden" name="_wpcf7_locale" value="en_US" />
                        <input type="hidden" name="_wpcf7_unit_tag" value="wpcf7-f1324-p1453-o1" />
                        <input type="hidden" name="_wpcf7_container_post" value="1453" />
                        <input type="hidden" name="_wpcf7_posted_data_hash" value="" />
                      </fieldset>
                      <div className="form-style-3">
                        <div className="columns_wrap">
                          <div className="column-1_2">
                            <p>
                              <span className="style-line icon-name">
                                <span className="wpcf7-form-control-wrap" data-name="your-name">
                                  <input
                                    size={40}
                                    maxLength={400}
                                    className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required fill_inited ${formErrors.name ? 'form-field-error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={formErrors.name ? "true" : "false"}
                                    placeholder="Your full name"
                                    value={formData.name}
                                    type="text"
                                    name="name"
                                    onChange={handleInputChange}
                                  />
                                  <span className="line"></span>
                                </span>
                                {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                              </span>
                            </p>
                          </div>
                          <div className="column-1_2">
                            <p>
                              <span className="style-line icon-email">
                                <span className="wpcf7-form-control-wrap" data-name="your-email">
                                  <input
                                    size={40}
                                    maxLength={400}
                                    className={`wpcf7-form-control wpcf7-email wpcf7-validates-as-required wpcf7-text wpcf7-validates-as-email fill_inited ${formErrors.email ? 'form-field-error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={formErrors.email ? "true" : "false"}
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    type="email"
                                    name="email"
                                    onChange={handleInputChange}
                                  />
                                  <span className="line"></span>
                                </span>
                                {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="columns_wrap">
                          <div className="column-1_2">
                            <p>
                              <span className="style-line icon-phone">
                                <span className="wpcf7-form-control-wrap" data-name="phone">
                                  <input
                                    size={40}
                                    maxLength={400}
                                    className={`wpcf7-form-control wpcf7-tel wpcf7-validates-as-required wpcf7-text wpcf7-validates-as-tel fill_inited ${formErrors.phone ? 'form-field-error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={formErrors.phone ? "true" : "false"}
                                    placeholder="Country code + phone number"
                                    value={formData.phone}
                                    type="tel"
                                    name="phone"
                                    onChange={handleInputChange}
                                  />
                                  <span className="line"></span>
                                </span>
                                {formErrors.phone && <span className="form-error">{formErrors.phone}</span>}
                              </span>
                            </p>
                          </div>
                          <div className="column-1_2">
                            <p>
                              <span className="style-line icon-subject">
                                <span className="wpcf7-form-control-wrap" data-name="quantity">
                                  <input
                                    size={40}
                                    maxLength={400}
                                    className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required fill_inited ${formErrors.quantity ? 'form-field-error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={formErrors.quantity ? "true" : "false"}
                                    placeholder="Quantity (e.g., 1, 5, 20, 100)"
                                    value={formData.quantity}
                                    type="text"
                                    name="quantity"
                                    onChange={handleInputChange}
                                  />
                                  <span className="line"></span>
                                </span>
                                {formErrors.quantity && <span className="form-error">{formErrors.quantity}</span>}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="columns_wrap">
                          <div className="column-1_1">
                            <p>
                              <span className="style-line icon-textarea">
                                <span className="wpcf7-form-control-wrap" data-name="your-message">
                                  <textarea
                                    cols={40}
                                    rows={10}
                                    maxLength={2000}
                                    className={`wpcf7-form-control wpcf7-textarea wpcf7-validates-as-required fill_inited ${formErrors.message ? 'form-field-error' : ''}`}
                                    aria-required="true"
                                    aria-invalid={formErrors.message ? "true" : "false"}
                                    placeholder="Describe what you want to create. Include size, purpose, or any details you already have."
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                  ></textarea>
                                  <span className="line"></span>
                                </span>
                                {formErrors.message && <span className="form-error">{formErrors.message}</span>}
                              </span>
                            </p>
                          </div>
                        </div>
                        <p>
                          <span className="wpcf7-submit-style">
                            <input
                              className="wpcf7-form-control wpcf7-submit has-spinner"
                              type="submit"
                              value={isSubmitting ? "Sending..." : "Send Enquiry"}
                              disabled={isSubmitting}
                            />
                            <span className="submit-style-in"></span>
                          </span>
                          <span className="wpcf7-spinner"></span>
                          <span className="wpcf7-form-control-wrap" data-name="acceptance">
                            <span className="wpcf7-form-control wpcf7-acceptance">
                              <span className="wpcf7-list-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    name="acceptance"
                                    checked={formData.acceptance}
                                    onChange={handleInputChange}
                                    aria-invalid={formErrors.acceptance ? "true" : "false"}
                                    className={`inited ${formErrors.acceptance ? 'form-field-error' : ''}`}
                                  />
                                  <span className="wpcf7-list-item-label">
                                    I agree that my data is collected
                                  </span>
                                </label>
                                {formErrors.acceptance && <span className="form-error">{formErrors.acceptance}</span>}
                              </span>
                            </span>
                          </span>
                          {submitError && <div className="submit-error">{submitError}</div>}
                        </p>
                      </div>
                      <div className="wpcf7-response-output" aria-hidden="true"></div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="elementor-section elementor-top-section elementor-element elementor-element-c3fe863 elementor-section-boxed elementor-section-height-default elementor-section-height-default sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
        <div className="elementor-container elementor-column-gap-extended">
          <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-e7af6e0 sc_content_align_inherit sc_layouts_column_icons_position_left sc_fly_static trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
            <div className="elementor-widget-wrap elementor-element-populated">
              <div className="elementor-element elementor-element-3c34191 sc_height_huge sc_fly_static elementor-widget elementor-widget-spacer trx_addons_parallax_layers_inited trx_addons_parallax_blocks_inited">
                <div className="elementor-widget-container">
                  <div className="elementor-spacer">
                    <div className="elementor-spacer-inner"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUsFormSection;
